import clsx from 'clsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { kebabCase } from 'lodash';
import React, { useLayoutEffect, useState } from 'react';

import { staticAssetPath } from '../../../../utils/paths';
import styles from './styles.module.scss';

const SHAREABLE_SCREENSHOT_ID = 'shareable-screenshot';

type RenderScreenshotContent = false | 'dark' | 'light';

export const useForumPostScreenshot = (title: string) => {
  const [renderScreenshotContent, setRenderScreenshotContent] =
    useState<RenderScreenshotContent>(false);

  // Clicking download renders a hidden ForumPostScreenshot component
  // and downloads its contents as a screenshot with html2canvas
  const onDownloadScreenshot = (darkMode) => {
    setRenderScreenshotContent(darkMode ? 'dark' : 'light');
  };

  const downloadScreenshot = (title: string, onComplete: () => void) => {
    html2canvas(document.getElementById(SHAREABLE_SCREENSHOT_ID), {
      onclone: function (clonedDoc) {
        clonedDoc.getElementById(SHAREABLE_SCREENSHOT_ID).style.display = 'block';
      },
      scale: 2,
    }).then((canvas) => {
      canvas.toBlob((blob) => {
        saveAs(blob, kebabCase(title.substring(0, 20)));
      });
      onComplete();
    });
  };

  useLayoutEffect(() => {
    if (renderScreenshotContent) {
      downloadScreenshot(title, () => setRenderScreenshotContent(false));
    }
  }, [renderScreenshotContent, title]);

  return { onDownloadScreenshot, renderScreenshotContent };
};

const getContainerWidthClass = (length: number) => {
  if (length > 1500) {
    return styles.xxl;
  } else if (length > 1250) {
    return styles.xl;
  } else if (length > 1000) {
    return styles.lg;
  } else if (length > 750) {
    return styles.md;
  }
  return '';
};

export default function ForumPostScreenshot({
  title,
  body,
  renderScreenshotContent,
  length,
}: {
  title: string;
  body: string;
  renderScreenshotContent: RenderScreenshotContent;
  length: number;
}) {
  const darkMode = renderScreenshotContent === 'dark';
  return (
    <div
      id={SHAREABLE_SCREENSHOT_ID}
      className={clsx(styles.forumPostScreenshotContainer, getContainerWidthClass(length), {
        [styles.formPostScreenshotContainerDark]: darkMode,
      })}
    >
      <div className={styles.forumPostScreenshotHeader}>
        {/* html2canvas doesn't allow cross-origin images */}
        {darkMode ? (
          <img
            src={staticAssetPath('share-forum-post-logo-dark.png')}
            alt="The Information Forum"
            width={316}
          />
        ) : (
          <img
            src={staticAssetPath('share-forum-post-logo.png')}
            alt="The Information Forum"
            width={316}
          />
        )}
      </div>
      <div className={styles.forumPostScreenshotTitle}>{title}</div>
      <div className={styles.forumPostScreenshotBody}>{body}</div>
    </div>
  );
}
